/**
 * ч.1319: панель платформи → «Відео-інструкції».
 *
 * Ролики до сторінок застосунку робить ШІ (сценарій зі статті довідки → озвучка → запис
 * екрана демо-акаунта → склейка → YouTube). Редактор лише дивиться, пише зауваження,
 * змінює налаштування і публікує — усе тут, без доступу до сервера.
 */
import { api } from './client';

/** ч.1413: у якому режимі робот знімає ролик. */
export type DemoMode = 'OWNER' | 'EDO' | 'USER';
export const DEMO_MODE_LABEL: Record<DemoMode, string> = { OWNER: 'Бізнес', EDO: 'ЕДО', USER: 'Користувач' };
export const DEMO_MODE_HINT: Record<DemoMode, string> = {
  OWNER: 'власний акаунт «Демо Облік» з повним меню',
  EDO: 'лише електронні документи, картки учасника ЕДО',
  USER: 'чужий акаунт «Приклад Плюс», демо-користувач у ньому бухгалтер',
};

/** ч.1327: до сторінки · сценарій через кілька сторінок · відповідь на часте питання. */
export type VideoKind = 'PAGE' | 'TOPIC' | 'FAQ';

export const KIND_LABEL: Record<VideoKind, string> = {
  PAGE: 'ролик до сторінки', TOPIC: 'сценарій', FAQ: 'часте питання',
};

export type VideoStatus =
  | 'NEW' | 'SCRIPTING' | 'RENDERING' | 'REVIEW' | 'APPROVED'
  | 'PUBLISHING' | 'PUBLISHED' | 'OUTDATED' | 'FAILED';

export interface VideoPage {
  route_pattern: string;
  slug: string;
  title: string;
  section: string | null;
  guide_id: string | null;
  status: VideoStatus | null;
  status_label: string;
  video_title: string | null;
  duration_sec: number | string | null;
  rendered_at: string | null;
  youtube_video_id: string | null;
  youtube_url: string | null;
  published_at: string | null;
  script_version: number | null;
  last_error: string | null;
  updated_at: string | null;
  open_remarks: number | null;
  /** ч.1331: довідка, за якою знято ролик, змінилась (null — ні). */
  help_changed_at?: string | null;
  help_change_note?: string | null;
  /** ч.1362: стаття довідки сторінки — для посилання в панель «Статті довідки». */
  article_id?: string | null;
}

/** ч.1362: які розділи статті сторінки показано в ролику (та сама міра, що в агента). */
export interface ArticleCoverage {
  covered: number;
  total: number;
  ratio: number;
  sections: { title: string; level: number; terms: number; matched: number; covered: boolean; missing: string[] }[];
  /** Сторінка документа: чи проведено документ у ролику по-справжньому (null — сторінка без проведення). */
  posted?: boolean | null;
}

export type StepAction = 'say' | 'click' | 'dblclick' | 'fill' | 'select' | 'hover' | 'scroll' | 'highlight' | 'goto' | 'press'
  | 'demo_key' | 'demo_file';

export interface StepTarget {
  role?: string | null;
  name?: string | null;
  text?: string | null;
  label?: string | null;
  placeholder?: string | null;
  testid?: string | null;
  exact?: boolean;
}

export interface ScriptStep {
  action: StepAction;
  target?: StepTarget | null;
  value?: string | null;
  narration: string;
  highlight?: boolean;
}

export interface VideoScript {
  title: string;
  description: string;
  tags: string[];
  steps: ScriptStep[];
}

export interface VideoRemark {
  id: string;
  text: string;
  status: 'NEW' | 'APPLIED' | 'FAILED';
  agent_reply: string | null;
  created_at: string;
  applied_at: string | null;
  author_name: string | null;
  author_email: string | null;
}

export interface VideoJob {
  id: string;
  kind: 'SCRIPT' | 'RENDER' | 'REVISE' | 'PUBLISH' | 'VALIDATE';
  status: 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED' | 'CANCELLED';
  attempts: number;
  last_error: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

/** ч.1327: рядок списку роликів-сценаріїв або роликів до питань. */
export interface VideoGuideListItem {
  guide_id: string;
  kind: VideoKind;
  title: string;
  brief: string | null;
  status: VideoStatus;
  status_label: string;
  duration_sec: number | string | null;
  rendered_at: string | null;
  youtube_url: string | null;
  script_version: number;
  last_error: string | null;
  updated_at: string | null;
  article_slug: string | null;
  question_title: string | null;
  visited_routes: string[];
  open_remarks: number | null;
  /** ч.1331: довідка, за якою знято ролик, змінилась (null — ні). */
  help_changed_at?: string | null;
  help_change_note?: string | null;
}

/** ч.1327: питання довідки і стан його ролика-відповіді. */
export interface FaqQuestion {
  slug: string;
  title: string;
  section: string | null;
  parent_route: string | null;
  guide_id: string | null;
  status: VideoStatus | null;
  status_label: string;
  duration_sec: number | string | null;
  youtube_url: string | null;
  /** ч.1331: довідка, за якою знято ролик, змінилась (null — ні). */
  help_changed_at?: string | null;
  help_change_note?: string | null;
}

/** ч.1370: стаття довідки (сторінка, документ, тема, питання) і стан її ролика — один список,
 *  як «Підтримка → Статті довідки». */
export type HelpArticleKind = 'page_ref' | 'page_doc' | 'topic' | 'question';
export interface VideoHelpItem {
  article_id: string;
  slug: string;
  kind: HelpArticleKind;
  title: string;
  section: string | null;
  section_order: number | null;
  route_pattern: string | null;
  parent_route: string | null;
  guide_id: string | null;
  status: VideoStatus | null;
  status_label: string;
  duration_sec: number | string | null;
  youtube_url: string | null;
  updated_at: string | null;
  open_remarks: number | null;
  help_changed_at?: string | null;
  help_change_note?: string | null;
}

/** Ролик до сторінки знімається за адресою, ролик за статтею (тема, питання) — за кодом статті. */
export const isPageArticle = (k: HelpArticleKind) => k === 'page_ref' || k === 'page_doc';

/** ч.1331: ролик-відповідь, чийого питання в довідці вже немає. */
export interface FaqOrphan {
  guide_id: string;
  title: string;
  brief: string | null;
  status: VideoStatus;
  status_label: string;
  duration_sec: number | string | null;
  youtube_url: string | null;
  help_changed_at: string | null;
  help_change_note: string | null;
}

export interface VideoGuide {
  id: string;
  kind: VideoKind;
  /** null — ролик не привʼязаний до однієї сторінки (сценарій, питання). */
  route_pattern: string | null;
  brief: string | null;
  start_path: string | null;
  visited_routes: string[];
  article_slug: string | null;
  /** ч.1331: довідка, за якою знято ролик, змінилась (null — ні). */
  help_changed_at?: string | null;
  help_change_note?: string | null;

  title: string;
  status: VideoStatus;
  status_label: string;
  script: VideoScript | null;
  script_version: number;
  settings_override: Record<string, unknown>;
  duration_sec: number | string | null;
  rendered_at: string | null;
  youtube_video_id: string | null;
  youtube_url: string | null;
  published_at: string | null;
  last_error: string | null;
  last_check_at: string | null;
  has_video: boolean;
  remarks: VideoRemark[];
  jobs: VideoJob[];
  /** ч.1370: `kind` — у ролика за статтею: питання чи тема. */
  article: { id?: string; slug: string; title: string; kind?: HelpArticleKind } | null;
  /** ч.1362: покриття статті роликом до сторінки (null — не ролик до сторінки або ще без сценарію). */
  coverage?: ArticleCoverage | null;
}

export interface VideoSettings {
  voice_provider: 'piper' | 'elevenlabs' | 'azure';
  voice: string;
  /** ч.1323: модель ElevenLabs (перелік — з кабінету, лише ті, що знають українську). */
  elevenlabs_model: string;
  speech_rate: number;
  pause_after_ms: number;
  viewport_width: number;
  viewport_height: number;
  zoom: number;
  theme: 'light' | 'dark';
  cursor_size: number;
  highlight_color: string;
  /** ч.1361: заставка — картинка-фон з назвою ролика або кольорове тло з текстом. */
  intro_style: 'image' | 'card';
  intro_title_prefix: string;
  intro_seconds: number;
  /** Блок назви на фоні заставки — у пікселях кадру 1920×1080. */
  intro_title_color: string;
  intro_title_size: number;
  intro_title_x: number;
  intro_title_y: number;
  intro_title_w: number;
  intro_title_h: number;
  /** ч.1361: кінцева заставка — відео, кольорове тло з текстом або без неї. */
  outro_style: 'clip' | 'card' | 'none';
  outro_text: string;
  outro_seconds: number;
  /** ч.1361: заклик підписатися всередині ролика (відео + озвучка текстом). */
  promo_enabled: boolean;
  promo_text: string;
  promo_at_percent: number;
  promo_min_video_seconds: number;
  promo_voice_delay: number;
  burn_subtitles: boolean;
  auto_rerender_outdated: boolean;
  youtube_privacy: 'unlisted' | 'public' | 'private';
}

/** ч.1361: файл оформлення ролика — стандартний (з коду) або завантажений редактором. */
export type BrandingKind = 'intro_bg' | 'promo' | 'outro';
export interface BrandingInfo {
  kind: BrandingKind;
  label: string;
  media: 'image' | 'video';
  /** Розширення через кому — для accept у полі вибору файлу. */
  accept: string;
  custom: boolean;
  present: boolean;
  size: number;
  updated_at: string | null;
}

/** Робітник відео (окремий контейнер): чи подавав сигнал останні 3 хвилини. */
export interface VideoWorkerState {
  online: boolean;
  seen_at: string | null;
  providers: Record<string, boolean>;
}

export interface VideoSettingsOut {
  settings: VideoSettings;
  voices: Record<string, { id: string; label: string }[]>;
  /** Синтезатори, які є в робітника (без робітника — усі false). */
  providers: Record<string, boolean>;
  worker: VideoWorkerState;
  /** ч.1361: файли оформлення (фон заставки, заклик, кінцева заставка). */
  branding?: Record<BrandingKind, BrandingInfo>;
  /** ч.1327: демо-акаунт — коли востаннє очищено, чи не впало очищення. */
  demo?: {
    demo_dirty: boolean; demo_reset_at: string | null; demo_reset_error: string | null;
    /** ч.1331: редактор попросив очистити зараз — робітник ще не виконав. */
    demo_reset_requested_at?: string | null;
    /** Що є в демо-акаунті після очищення. */
    content?: string[];
    email?: string | null;
  };
  /** Кабінет ElevenLabs за ключем сервера: моделі, залишок символів, голос за замовчуванням. */
  elevenlabs: {
    models: { id: string; label: string }[];
    quota: { used: number | null; limit: number | null; tier: string | null; reset_unix: number | null } | null;
    error: string | null;
    default_voice: string;
  };
}

export interface VideoPagesOut {
  items: VideoPage[];
  worker: VideoWorkerState;
}

export interface DevRequest {
  id: string;
  text: string;
  status: 'NEW' | 'DONE';
  created_at: string;
  author_name: string | null;
  author_email: string | null;
  guide_title: string | null;
}

/** ч.1363: памʼять агента — правила і вимова для всіх роликів. */
export type AgentMemoryKind = 'RULE' | 'SAY';

export interface AgentMemoryItem {
  id: string;
  kind: AgentMemoryKind;
  /** Правило або слово так, як воно написано в тексті диктора. */
  text: string;
  /** Як слово вимовляти (лише для SAY). */
  spoken: string | null;
  active: boolean;
  guide_id: string | null;
  guide_title: string | null;
  remark_id: string | null;
  remark_text: string | null;
  source_note: string | null;
  created_at: string;
  updated_at: string;
  author_name: string | null;
  author_email: string | null;
}

export interface AgentMemoryOut {
  items: AgentMemoryItem[];
  max_rules: number;
  max_say: number;
}

export interface YoutubeStatus {
  configured: boolean;
  connected: boolean;
  channel_title: string | null;
  channel_id: string | null;
  connected_at: string | null;
}

export type GuideAction = 'regenerate' | 'rerender' | 'approve' | 'delete' | 'help_ok';

const SILENT = { headers: { 'X-Silent-Toast': '1' } };
const BASE = '/platform/video';

export const platformVideo = {
  pages: () => api.get<VideoPagesOut>(`${BASE}/pages`).then((r) => r.data),
  /** ч.1362: `brief` — завдання агенту від редактора, одне на всі позначені сторінки (необовʼязкове). */
  create: (routes: string[], brief?: string, demoMode: DemoMode = 'OWNER') =>
    api.post<{ created: number }>(`${BASE}/guides`, {
      routes, demo_mode: demoMode, ...(brief?.trim() ? { brief: brief.trim() } : {}),
    }, SILENT).then((r) => r.data),
  guide: (id: string) => api.get<VideoGuide>(`${BASE}/guides/${id}`).then((r) => r.data),
  /** ч.1327: ролики-сценарії або ролики до частих питань. */
  guides: (kind: 'TOPIC' | 'FAQ') =>
    api.get<{ items: VideoGuideListItem[]; worker: VideoWorkerState }>(`${BASE}/guides`, { params: { kind } })
      .then((r) => r.data),
  createTopic: (title: string, brief: string, demoMode: DemoMode = 'OWNER') =>
    api.post<{ id: string }>(`${BASE}/topics`, { title, brief, demo_mode: demoMode }, SILENT).then((r) => r.data),
  /** ч.1370: уся довідка одним списком зі станом роликів. */
  help: () =>
    api.get<{ items: VideoHelpItem[]; orphans: FaqOrphan[]; worker: VideoWorkerState }>(`${BASE}/help`)
      .then((r) => r.data),
  faqQuestions: () =>
    api.get<{ items: FaqQuestion[]; orphans: FaqOrphan[] }>(`${BASE}/faq-questions`).then((r) => r.data),
  /** ч.1331: перепривʼязати ролик-відповідь до іншого питання довідки. */
  relink: (id: string, slug: string) =>
    api.post(`${BASE}/guides/${id}/relink`, { slug }, SILENT).then((r) => r.data),
  /** ч.1331: очистити й наповнити демо-акаунт зараз. */
  demoReset: () => api.post<{ result: string }>(`${BASE}/demo/reset`, {}, SILENT).then((r) => r.data),
  createFaq: (slugs: string[], demoMode: DemoMode = 'OWNER') =>
    api.post<{ created: number }>(`${BASE}/faq`, { slugs, demo_mode: demoMode }, SILENT).then((r) => r.data),
  patchGuide: (id: string, patch: { title?: string; brief?: string; demo_mode?: DemoMode }) =>
    api.patch(`${BASE}/guides/${id}`, patch, SILENT).then((r) => r.data),
  action: (id: string, action: GuideAction) =>
    api.post<{ result: string }>(`${BASE}/guides/${id}/action`, { action }, SILENT).then((r) => r.data),
  remark: (id: string, text: string) =>
    api.post<{ id: string }>(`${BASE}/guides/${id}/remarks`, { text }, SILENT).then((r) => r.data),
  saveScript: (id: string, script: VideoScript) =>
    api.put(`${BASE}/guides/${id}/script`, script, SILENT).then((r) => r.data),
  /** Файл ролика з авторизацією (у <video> не передати заголовок) → посилання на blob. */
  fileUrl: async (id: string, kind: 'mp4' | 'srt' = 'mp4') => {
    const r = await api.get<Blob>(`${BASE}/guides/${id}/file`, { params: { kind }, responseType: 'blob' });
    return URL.createObjectURL(r.data);
  },
  settings: () => api.get<VideoSettingsOut>(`${BASE}/settings`).then((r) => r.data),
  saveSettings: (s: VideoSettings) =>
    api.put<{ settings: VideoSettings }>(`${BASE}/settings`, s, SILENT).then((r) => r.data),
  /** «Прослухати» голос ElevenLabs: коротка фраза українською (або свій текст) → посилання на аудіо. */
  sample: async (voice: string, elevenlabsModel: string, speechRate: number, text?: string) => {
    const r = await api.post<Blob>(`${BASE}/settings/sample`,
      { voice, elevenlabs_model: elevenlabsModel, speech_rate: speechRate, ...(text ? { text } : {}) },
      { responseType: 'blob', headers: { 'X-Silent-Toast': '1' } });
    return URL.createObjectURL(r.data);
  },
  /** ч.1361: файл оформлення з авторизацією → посилання на blob (для <img> / <video>). */
  brandingFileUrl: async (kind: BrandingKind) => {
    const r = await api.get<Blob>(`${BASE}/branding/${kind}/file`, { responseType: 'blob' });
    return URL.createObjectURL(r.data);
  },
  uploadBranding: (kind: BrandingKind, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.put<{ branding: BrandingInfo }>(`${BASE}/branding/${kind}`, fd, SILENT).then((r) => r.data.branding);
  },
  resetBranding: (kind: BrandingKind) =>
    api.delete<{ branding: BrandingInfo }>(`${BASE}/branding/${kind}`, SILENT).then((r) => r.data.branding),
  /** Кадр заставки з назвою за незбереженими налаштуваннями форми → посилання на PNG. */
  introPreview: async (title: string, settings: VideoSettings) => {
    const r = await api.post<Blob>(`${BASE}/branding/intro-preview`, { title, settings },
      { responseType: 'blob', headers: { 'X-Silent-Toast': '1' } });
    return URL.createObjectURL(r.data);
  },
  memory: () => api.get<AgentMemoryOut>(`${BASE}/memory`).then((r) => r.data),
  addMemory: (kind: AgentMemoryKind, text: string, spoken?: string) =>
    api.post<{ id: string }>(`${BASE}/memory`, { kind, text, ...(kind === 'SAY' ? { spoken } : {}) }, SILENT)
      .then((r) => r.data),
  patchMemory: (id: string, patch: { text?: string; spoken?: string; active?: boolean }) =>
    api.patch(`${BASE}/memory/${id}`, patch, SILENT).then((r) => r.data),
  deleteMemory: (id: string) => api.delete(`${BASE}/memory/${id}`, SILENT).then((r) => r.data),
  requests: () => api.get<{ items: DevRequest[] }>(`${BASE}/requests`).then((r) => r.data.items),
  addRequest: (text: string, guideId?: string | null) =>
    api.post<{ id: string }>(`${BASE}/requests`, { text, guide_id: guideId ?? null }, SILENT).then((r) => r.data),
  youtube: () => api.get<YoutubeStatus>(`${BASE}/youtube`).then((r) => r.data),
  youtubeConnect: () => api.post<{ url: string }>(`${BASE}/youtube/connect`, {}, SILENT).then((r) => r.data),
  youtubeDisconnect: () => api.post(`${BASE}/youtube/disconnect`, {}, SILENT).then((r) => r.data),
};

/** Ролик зайнятий робітником — дії недоступні, сторінка оновлюється сама. */
export const BUSY_STATUSES: VideoStatus[] = ['SCRIPTING', 'RENDERING', 'PUBLISHING'];

export const ACTION_LABEL: Record<StepAction, string> = {
  say: 'розповідь', click: 'клік', dblclick: 'подвійний клік', fill: 'ввід', select: 'вибір', hover: 'наведення',
  scroll: 'прокрутка', highlight: 'підсвітити', goto: 'перехід', press: 'клавіша',
  demo_key: 'тестовий ключ', demo_file: 'демо-файл',
};

/** Що робить крок, людською мовою: «клік: кнопка «+ Новий документ реалізації»». */
export function targetLabel(t: StepTarget | null | undefined): string {
  if (!t) return '';
  const what = t.name || t.text || t.label || t.placeholder || t.testid || '';
  const role: Record<string, string> = {
    button: 'кнопка', link: 'посилання', textbox: 'поле', combobox: 'список', tab: 'вкладка',
    checkbox: 'галка', heading: 'заголовок', row: 'рядок', cell: 'клітинка', menuitem: 'пункт меню',
  };
  const r = t.role ? (role[t.role] ?? t.role) : '';
  return [r, what ? `«${what}»` : ''].filter(Boolean).join(' ');
}

export function fmtDuration(v: number | string | null | undefined): string {
  const n = Number(v);
  if (!v || !Number.isFinite(n)) return '—';
  const m = Math.floor(n / 60);
  const s = Math.round(n % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
